// src/pages/team/TeamListPage.tsx

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import { ListMyTeamInvitations } from '@core/api/endpoints/team/list_my_team_invitations';
import { Button, Input, Badge, ErrorAlert } from '@react/components';
import { TeamCreateDialog } from '@/features/team/TeamCreateDialog';
import {
  sortTeamsByActivity,
  teamFilters,
  teamFilterToType,
  teamStatusMap,
  teamTypeMap,
  teamVisibilityMap,
  invitationStatusMap,
  getStatusMapping,
  getInitials,
  getAvatarColor,
  formatRelativeTime,
  getEntityId,
  type TeamFilter,
} from '@/features/team/logic';

type PageTab = 'teams' | 'invitations';

export const TeamListPage = observer(() => {
  const store = useTeam();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);

  const [teams, setTeams] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TeamFilter>('all');

  // Page-level tab
  const pageTab = (searchParams.get('tab') as PageTab) || 'teams';
  const setPageTab = (tab: PageTab) => {
    setSearchParams(tab === 'teams' ? {} : { tab });
  };

  // Invitations state
  const [invitations, setInvitations] = useState<any[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);

  // Load teams — search and type filter are applied server-side via the
  // standard PaginatedRequest FilterGroup mechanism.
  const loadTeams = useCallback((searchTerm: string, type: TeamFilter) => {
    setIsLoading(true);
    setError(null);

    const rules: any[] = [];
    if (searchTerm.trim()) {
      rules.push({
        field: 'name',
        operator: 'FILTER_OPERATOR_LIKE',
        value: { string_value: searchTerm.trim() },
      });
    }
    const mappedType = teamFilterToType[type];
    if (mappedType) {
      rules.push({
        field: 'team_type',
        operator: 'FILTER_OPERATOR_EQ',
        value: { string_value: mappedType },
      });
    }

    return store.ListTeams({
      pagination: { cursor: '', limit: 100, direction: 'forward' as const },
      sort: [],
      view: 0,
      ...(rules.length > 0
        ? { filters: { logic: 'LOGIC_OPERATOR_AND', rules, groups: [] } as any }
        : {}),
    })
      .then(() => {
        setTeams(sortTeamsByActivity(store.items));
      })
      .catch((err: any) => setError(err instanceof Error ? err.message : t('team.loadError')))
      .finally(() => setIsLoading(false));
  }, [store, t]);

  // Debounced server-side search: re-query when the search text or type
  // filter changes; the initial load (empty search) fires immediately.
  useEffect(() => {
    const handle = setTimeout(
      () => loadTeams(search, typeFilter),
      search ? 300 : 0,
    );
    return () => clearTimeout(handle);
  }, [search, typeFilter, loadTeams]);

  useEffect(() => {
    if (!isLoading) {
      setTeams(sortTeamsByActivity(store.items));
    }
  }, [store.items]);

  // Load invitations when tab is active
  const loadInvitations = useCallback(() => {
    setInvitationsLoading(true);
    setInvitationsError(null);
    ListMyTeamInvitations()
      .then((result) => {
        setInvitations(result.invitations?.invitations ?? []);
      })
      .catch((err: any) => setInvitationsError(err instanceof Error ? err.message : t('team.loadError')))
      .finally(() => setInvitationsLoading(false));
  }, [t]);

  useEffect(() => {
    if (pageTab === 'invitations') {
      loadInvitations();
    }
  }, [pageTab, loadInvitations]);

  const pendingInvitations = useMemo(
    () => invitations.filter((inv) => inv.status === 'INVITATION_STATUS_PENDING'),
    [invitations],
  );

  const handleAccept = async (invId: string) => {
    try {
      await store.AcceptTeamInvitation(invId, {});
      loadInvitations();
    } catch {
      // error handled by store
    }
  };

  const handleDecline = async (invId: string) => {
    try {
      await store.DeclineTeamInvitation(invId, {});
      loadInvitations();
    } catch {
      // error handled by store
    }
  };

  // Search and type filtering happen server-side (see loadTeams); the
  // loaded list is already the filtered result.
  const filtered = teams;

  return (
    <div className="space-y-4">
      {/* Page-level tabs */}
      <div className="flex gap-1 border-b border-border mb-4">
        <button
          onClick={() => setPageTab('teams')}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            pageTab === 'teams'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('team.list')}
        </button>
        <button
          onClick={() => setPageTab('invitations')}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            pageTab === 'invitations'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('team.invitations')}
          {pendingInvitations.length > 0 && (
            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary">
              {pendingInvitations.length}
            </span>
          )}
        </button>
      </div>

      {pageTab === 'teams' && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('team.list')}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{t('team.listDescription')}</p>
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)}>{t('team.create')}</Button>
          </div>

          {/* Search */}
          <Input
            placeholder={t('team.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />

          {/* Type filter tabs */}
          <div className="flex gap-1 border-b border-border">
            {teamFilters.map(f => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  typeFilter === f.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>

          {/* Error */}
          {(error || store.error) && (
            <ErrorAlert error={store.error || error} dismissible onDismiss={() => { setError(null); store.clearError(); }} />
          )}

          {/* Loading */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                  <div className="h-11 w-11 rounded-lg bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                  </div>
                  <div className="h-5 w-16 rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            /* Empty state */
            <div className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                </svg>
              </div>
              <p className="text-lg font-medium text-foreground">
                {search ? t('team.noSearchResults') : t('team.noItems')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {search ? t('team.tryDifferentSearch') : t('team.noItemsDescription')}
              </p>
              {!search && (
                <Button size="sm" className="mt-4" onClick={() => setShowCreate(true)}>{t('team.create')}</Button>
              )}
            </div>
          ) : (
            /* Team list */
            <ul className="divide-y divide-border -mx-2">
              {filtered.map(team => (
                <TeamRow key={getEntityId(team)} team={team} locale={locale} />
              ))}
            </ul>
          )}
        </>
      )}

      {pageTab === 'invitations' && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('team.myInvitations')}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{t('team.myInvitationsDescription')}</p>
            </div>
          </div>

          {/* Error */}
          {(invitationsError || store.error) && (
            <ErrorAlert error={store.error || invitationsError} dismissible onDismiss={() => { setInvitationsError(null); store.clearError(); }} />
          )}

          {/* Loading */}
          {invitationsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 animate-pulse">
                  <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-muted" />
                    <div className="h-3 w-1/2 rounded bg-muted" />
                  </div>
                  <div className="h-8 w-20 rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : invitations.length === 0 ? (
            /* Empty state */
            <div className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
              </div>
              <p className="text-lg font-medium text-foreground">{t('team.noMyInvitations')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('team.noMyInvitationsDescription')}</p>
            </div>
          ) : (
            /* Invitations list */
            <div className="space-y-3">
              {invitations.map(inv => {
                const invId = getEntityId(inv);
                const isPending = inv.status === 'INVITATION_STATUS_PENDING';
                const statusInfo = getStatusMapping(invitationStatusMap, inv.status || 'INVITATION_STATUS_PENDING');
                return (
                  <div key={invId} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 hover:shadow-sm transition-all">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{inv.team_name || t('team.title')}</span>
                        <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 space-x-3">
                        {inv.inviter?.display_name && <span>{t('team.memberInvitedBy')}: {inv.inviter.display_name}</span>}
                        <span>{formatRelativeTime(inv.sent_at, locale)}</span>
                      </div>
                      {inv.message && <p className="text-xs text-muted-foreground mt-1 italic">"{inv.message}"</p>}
                    </div>
                    {isPending && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => handleAccept(invId)}>{t('common.accept')}</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDecline(invId)}>{t('common.decline')}</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <TeamCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          loadTeams(search, typeFilter);
          if (id) navigate(`/teams/${id}`);
        }}
      />
    </div>
  );
});

function TeamRow({ team, locale }: { team: any; locale: string }) {
  const { t } = useI18n();
  const id = getEntityId(team);
  const status = getStatusMapping(teamStatusMap, team.status);
  const typeInfo = teamTypeMap[team.team_type];
  const visInfo = teamVisibilityMap[team.visibility];
  const memberCount = team.members_count ?? team.member_count ?? 0;

  return (
    <li>
      <Link
        to={`/teams/${id}`}
        className="flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-muted/50"
      >
        {/* Avatar */}
        <div className="flex-shrink-0">
          {team.avatar?.url ? (
            <img
              src={team.avatar.url}
              alt={team.name}
              className="h-11 w-11 rounded-lg object-cover"
            />
          ) : (
            <div
              className="flex h-11 w-11 items-center justify-center rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: getAvatarColor(team.name || '') }}
            >
              {getInitials(team.name || '')}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate text-sm font-semibold text-foreground">
                {team.name || t('common.untitled')}
              </span>
              <Badge variant={status.variant as any} size="sm">
                {t(status.labelKey)}
              </Badge>
            </div>
            <span className="flex-shrink-0 text-xs text-muted-foreground">
              {formatRelativeTime(team.updated_at ?? team.created_at, locale)}
            </span>
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <p className="truncate text-sm text-muted-foreground">
              {team.description || (typeInfo ? t(typeInfo.labelKey) : '')}
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              {visInfo && (
                <span className="text-xs text-muted-foreground">
                  {t(visInfo.labelKey)}
                </span>
              )}
              {memberCount > 0 && (
                <span className="text-xs text-muted-foreground" title={t('team.memberCount_other', { count: memberCount })}>
                  <svg className="inline h-3 w-3 mr-0.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                  </svg>
                  {memberCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}

export default TeamListPage;
